import React, { useState } from "react";
import { Box, IconButton, Button } from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

const COLORS = {
  brown: "#674934",
  border: "#D9CFC7",
  placeholder: "#E9E4DE",
};

const ImageCarousel = ({ images = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  if (!images || images.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: { xs: 220, md: 400, lg: 500 },
          backgroundColor: COLORS.placeholder,
          borderRadius: 2,
        }}
      />
    );
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const openLightbox = () => {
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  return (
    <>
      {/* MAIN CARD – only 1 image shown + small button */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: 220, md: 400, lg: 500 },
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: COLORS.placeholder,
          backgroundImage: `url(${images[0]})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Small button to open gallery */}
        <Button
          variant="contained"
          onClick={(e) => {
            e.stopPropagation(); // prevent parent (Read more) click from firing
            openLightbox();
          }}
          sx={{
            position: "absolute",
            right: 12,
            bottom: 12,
            textTransform: "none",
            fontSize: 12,
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            backgroundColor: COLORS.brown,
            "&:hover": {
              backgroundColor: COLORS.brown,
              opacity: 0.9,
            },
          }}
        >
          View Gallery
        </Button>
      </Box>

      {/* SIMPLE OVERLAY LIGHTBOX (no MUI Modal) */}
      {isLightboxOpen && (
        <Box
          onClick={closeLightbox} // click on backdrop closes
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(6px)",
            zIndex: 2000, // higher than dialogs/sheets
          }}
        >
          {/* Stop clicks inside content from closing immediately */}
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "85vh",
              borderRadius: 2,
              bgcolor: "#111",
              p: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: 24,
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={closeLightbox}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "#fff",
                zIndex: 3,
              }}
            >
              ✕
            </IconButton>

            {/* Main image */}
            <Box
              component="img"
              src={images[currentIndex]}
              alt={`image-${currentIndex + 1}`}
              sx={{
                maxWidth: "100%",
                maxHeight: "65vh",
                objectFit: "contain",
                borderRadius: 1.5,
              }}
            />

            {/* Arrows (inside lightbox only, if multiple images) */}
            {images.length > 1 && (
              <>
                <IconButton
                  onClick={goToPrevious}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    color: "#fff",
                    width: 40,
                    height: 40,
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.9)",
                    },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>

                <IconButton
                  onClick={goToNext}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    color: "#fff",
                    width: 40,
                    height: 40,
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.9)",
                    },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            )}

            {/* Thumbnails row – shows ALL images */}
            {images.length > 1 && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  gap: 1,
                  overflowX: "auto",
                  maxWidth: "100%",
                  px: 0.5,
                }}
              >
                {images.map((img, index) => (
                  <Box
                    key={index}
                    component="img"
                    src={img}
                    alt={`thumb-${index + 1}`}
                    onClick={() => setCurrentIndex(index)}
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 1,
                      objectFit: "cover",
                      cursor: "pointer",
                      border:
                        index === currentIndex
                          ? `2px solid ${COLORS.brown}`
                          : "2px solid transparent",
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default ImageCarousel;
